/**
 * Typed wrappers around the backend's `/auth/*` endpoints. Pure
 * functions that return parsed bodies — TanStack Query handles
 * caching, retries, and the "current user" cache key elsewhere.
 *
 * `credentials: 'include'` is non-negotiable: the cookie is httpOnly
 * and rides every request. The server's CORS allowlist must match the
 * studio's origin exactly.
 */

export interface UserSummary {
  id: string;
  email: string;
  email_verified: boolean;
  /** Authorization role. `standard` is the default; `teacher` /
   *  `moderator` / `admin` unlock additional surfaces. */
  role: 'standard' | 'teacher' | 'moderator' | 'admin';
  /** True when an account has been suspended by a moderator. The
   *  studio shows a notice and disables write affordances. */
  suspended: boolean;
  /** True when this row is a child profile under a parent account.
   *  The frontend swaps kid-friendly copy + the publish flow into
   *  parent-consent mode when this is set. */
  is_child?: boolean;
  /** Parent-set display name for child profiles. Null for adults. */
  display_name?: string | null;
  /** Phase 4.5b — set on classroom-student profiles. The kid's Home
   *  uses this to surface pinned assignments + the class gallery
   *  link. NULL for parent-linked children and standalone adults. */
  classroom_id?: string | null;
  /** Phase 5 — kid-chosen handle for the public profile at
   *  `/u/<username>`. Null until claimed. */
  username?: string | null;
}

export interface MagicLinkResponse {
  sent: boolean;
  /** Only populated outside production so dev / e2e can complete
   *  without a real email pipeline. */
  link: string | null;
}

const DEFAULT_API_BASE_URL = 'http://localhost:8000';

/** Resolve the API base URL from Vite env. Tests and exotic deploys
 *  override via VITE_API_URL. */
export function apiBaseUrl(): string {
  const env = import.meta.env?.VITE_API_URL;
  if (typeof env === 'string' && env.length > 0) return env;
  return DEFAULT_API_BASE_URL;
}

/**
 * One-shot fetch wrapper. Throws an `AuthApiError` on non-2xx so
 * TanStack Query treats it as a failed query. Always sends + receives
 * cookies.
 */
export class AuthApiError extends Error {
  status: number;
  /** The server's `detail` field, if any — kid-friendly copy comes
   *  back here for 4xx errors. */
  detail: string | null;

  constructor(status: number, detail: string | null, fallback: string) {
    super(detail || fallback);
    this.name = 'AuthApiError';
    this.status = status;
    this.detail = detail;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = `${apiBaseUrl()}${path}`;
  const headers = new Headers(init.headers ?? {});
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  const response = await fetch(url, {
    ...init,
    headers,
    credentials: 'include',
  });
  if (response.status === 204) {
    // No-content shape; T is whatever the caller declares (usually `void`).
    return undefined as T;
  }
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Empty / non-JSON bodies — fall through with body=null.
  }
  if (!response.ok) {
    const detail =
      body && typeof body === 'object' && 'detail' in body && typeof body.detail === 'string'
        ? body.detail
        : null;
    throw new AuthApiError(
      response.status,
      detail,
      `Request to ${path} failed (${response.status}).`,
    );
  }
  return body as T;
}

export interface SignupArgs {
  email: string;
  password: string;
  /** Required at signup since 5.1 — drives the COPPA under-13 gate
   *  and the gallery publish gate. Allowing it to be optional was
   *  the loophole that let kids bypass both. */
  birthYear: number;
}

export async function signup(args: SignupArgs): Promise<UserSummary> {
  return request<UserSummary>('/auth/signup', {
    method: 'POST',
    body: JSON.stringify({
      email: args.email,
      password: args.password,
      birth_year: args.birthYear,
    }),
  });
}

export async function signin(email: string, password: string): Promise<UserSummary> {
  return request<UserSummary>('/auth/signin', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export async function signout(): Promise<void> {
  return request<void>('/auth/signout', { method: 'POST' });
}

/** Permanently delete the signed-in account. The server CASCADEs
 *  the delete through sessions and projects, so a single call wipes
 *  everything the kid put on the server. */
export async function deleteAccount(): Promise<void> {
  return request<void>('/auth/me', { method: 'DELETE' });
}

export async function me(): Promise<UserSummary | null> {
  try {
    return await request<UserSummary>('/auth/me', { method: 'GET' });
  } catch (err) {
    // 401 means "not signed in" — that's a valid query result, not
    // an error to surface to the kid.
    if (err instanceof AuthApiError && err.status === 401) return null;
    throw err;
  }
}

export async function requestMagicLink(email: string): Promise<MagicLinkResponse> {
  return request<MagicLinkResponse>('/auth/magic-link', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function consumeMagicLink(token: string): Promise<UserSummary> {
  return request<UserSummary>('/auth/magic-link/consume', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

export interface ChangePasswordArgs {
  currentPassword: string;
  newPassword: string;
}

/** Rotate the signed-in user's password. The server verifies the
 *  current password, then revokes every existing session and re-mints
 *  the cookie so this tab stays signed in but every other device is
 *  booted. */
export async function changePassword(args: ChangePasswordArgs): Promise<void> {
  return request<void>('/auth/me/password', {
    method: 'POST',
    body: JSON.stringify({
      current_password: args.currentPassword,
      new_password: args.newPassword,
    }),
  });
}

export interface PasswordResetResponse {
  sent: boolean;
  /** Only populated outside production so dev / e2e can complete
   *  without a real email pipeline. */
  link: string | null;
}

/** Forgot-password kickoff. The response shape is the same whether or
 *  not the email matches a registered account — server-side
 *  enumeration defence. */
export async function requestPasswordReset(email: string): Promise<PasswordResetResponse> {
  return request<PasswordResetResponse>('/auth/password-reset/request', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export interface ConsumePasswordResetArgs {
  token: string;
  newPassword: string;
}

/** Trade a reset token + new password for a fresh session. The kid
 *  lands signed in on success — no need to bounce them through the
 *  signin form right after. */
export async function consumePasswordReset(args: ConsumePasswordResetArgs): Promise<UserSummary> {
  return request<UserSummary>('/auth/password-reset/consume', {
    method: 'POST',
    body: JSON.stringify({
      token: args.token,
      new_password: args.newPassword,
    }),
  });
}

// --- Parent-linked accounts (Phase 4.5a) -------------------------------

export interface ChildSummary {
  id: string;
  display_name: string;
  birth_year: number | null;
  suspended: boolean;
}

export interface AddChildArgs {
  displayName: string;
  birthYear: number;
  pin: string;
}

export interface ChildSigninArgs {
  parentEmail: string;
  displayName: string;
  pin: string;
}

export async function addChild(args: AddChildArgs): Promise<ChildSummary> {
  return request<ChildSummary>('/auth/me/children', {
    method: 'POST',
    body: JSON.stringify({
      display_name: args.displayName,
      birth_year: args.birthYear,
      pin: args.pin,
    }),
  });
}

export async function listChildren(): Promise<ChildSummary[]> {
  return request<ChildSummary[]>('/auth/me/children');
}

export async function deleteChild(childId: string): Promise<void> {
  return request<void>(`/auth/me/children/${encodeURIComponent(childId)}`, {
    method: 'DELETE',
  });
}

export async function childSignin(args: ChildSigninArgs): Promise<UserSummary> {
  return request<UserSummary>('/auth/children/signin', {
    method: 'POST',
    body: JSON.stringify({
      parent_email: args.parentEmail,
      display_name: args.displayName,
      pin: args.pin,
    }),
  });
}

export interface StudentSigninArgs {
  /** 8-char ambiguity-stripped class code from the teacher. We
   *  uppercase + strip spaces before sending so a kid mistyping
   *  case doesn't fail. */
  classCode: string;
  displayName: string;
  pin: string;
}

/** Phase 4.5b — sign a kid in to a teacher-managed classroom. The
 *  backend matches case-insensitively but we still pass an
 *  upper-cased value so the on-the-wire shape mirrors the teacher's
 *  printed sign. */
export async function studentSignin(args: StudentSigninArgs): Promise<UserSummary> {
  return request<UserSummary>('/auth/students/signin', {
    method: 'POST',
    body: JSON.stringify({
      class_code: args.classCode.replace(/\s+/g, '').toUpperCase(),
      display_name: args.displayName,
      pin: args.pin,
    }),
  });
}

export interface ParentConsentSummary {
  submission_id: string;
  child_display_name: string;
  project_name: string;
  project_kind: 'image' | 'audio' | 'pose';
  decision: 'pending' | 'approved' | 'denied' | 'expired';
  decided_at: string | null;
  expires_at: string;
}

export async function viewParentConsent(token: string): Promise<ParentConsentSummary> {
  return request<ParentConsentSummary>(`/auth/parent-consent/${encodeURIComponent(token)}`);
}

export async function decideParentConsent(
  token: string,
  decision: 'approve' | 'deny',
): Promise<ParentConsentSummary> {
  return request<ParentConsentSummary>(`/auth/parent-consent/${encodeURIComponent(token)}`, {
    method: 'POST',
    body: JSON.stringify({ decision }),
  });
}
